import { useState, useEffect, useCallback } from "react";
import { GitCompare, Download, Trophy, AlertTriangle, CheckCircle, MinusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { KPICard } from "@/components/KPICard";
import { getLoanComparison, downloadPdfUrl, exportCsvUrl } from "@/lib/api";

const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 });

const riskStyles = {
  Low: { bg: "bg-[#2D4A3E]", text: "text-white", label: "Low Risk" },
  Moderate: { bg: "bg-[#E3B505]", text: "text-[#1C1917]", label: "Moderate Risk" },
  High: { bg: "bg-[#E07A5F]", text: "text-white", label: "High Risk" },
};

const recStyles = {
  "Good Fit": { icon: CheckCircle, color: "text-[#2D4A3E]", bg: "bg-[#2D4A3E]/10" },
  "Borderline": { icon: MinusCircle, color: "text-[#E3B505]", bg: "bg-[#E3B505]/10" },
  "Not Supportable": { icon: AlertTriangle, color: "text-[#E07A5F]", bg: "bg-[#E07A5F]/10" },
};

export default function LoanComparison({ refreshKey, analysisId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLoanComparison(analysisId);
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [analysisId]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const handleDownload = () => {
    window.open(downloadPdfUrl("comparison", analysisId), "_blank");
  };

  const handleCsvExport = () => {
    window.open(exportCsvUrl("offers", analysisId), "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.comparison?.length) {
    return (
      <div className="bg-white border border-[#E7E5DF] rounded-md p-12 text-center" data-testid="comparison-empty">
        <GitCompare className="w-12 h-12 text-[#8A9A86] mx-auto mb-4" />
        <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-2">
          No Comparison Available
        </h3>
        <p className="text-[#57534E] text-sm">
          Add bank statement data and loan offers to generate a comparison.
        </p>
      </div>
    );
  }

  const { comparison, cash_flow_summary: cfs } = data;
  const best = comparison[0];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="loan-comparison-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-semibold text-[#1C1917]">
            Loan Comparison
          </h2>
          <p className="text-sm text-[#57534E] mt-1">
            {comparison.length} offers ranked by overall value
          </p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <Button
            data-testid="download-comparison-btn"
            onClick={handleDownload}
            variant="outline"
            className="border-[#E7E5DF] hover:bg-[#F1EFEA]"
          >
            <Download className="w-4 h-4 mr-1" />
            PDF
          </Button>
          <Button
            data-testid="export-comparison-csv-btn"
            onClick={handleCsvExport}
            variant="outline"
            className="border-[#E7E5DF] hover:bg-[#F1EFEA]"
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
        </div>
      </div>

      {/* Cash Flow Context */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPICard title="Free Cash Flow" value={fmtMoney(cfs.free_cash_flow)} subtitle="Available for new debt" variant={cfs.free_cash_flow > 5000 ? "success" : "warning"} />
        <KPICard title="Current DSCR" value={cfs.dscr >= 99 ? "N/A" : `${cfs.dscr}x`} subtitle="Before new loan" variant={cfs.dscr >= 1.5 ? "success" : "warning"} />
        <KPICard title="Existing Debt Service" value={fmtMoney(cfs.existing_monthly_debt_service)} subtitle="Monthly obligations" />
      </div>

      {/* Ranking Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {comparison.map((offer, idx) => {
          const risk = riskStyles[offer.risk_level] || riskStyles.High;
          const rec = recStyles[offer.recommendation] || recStyles["Not Supportable"];
          const RecIcon = rec.icon;
          return (
            <div
              key={offer.id}
              data-testid={`comparison-card-${offer.id}`}
              className={`bg-white border rounded-md p-5 relative ${
                idx === 0 ? "border-[#1A362D] border-2" : "border-[#E7E5DF]"
              } hover:shadow-lg hover:-translate-y-1 transition-all duration-200`}
            >
              {idx === 0 && (
                <div className="absolute -top-3 left-4 flex items-center gap-1 bg-[#1A362D] text-white text-xs font-bold px-3 py-1 rounded-full">
                  <Trophy className="w-3 h-3" /> Best Option
                </div>
              )}
              <div className="flex items-center justify-between mb-3 mt-1">
                <span className="text-xs font-['IBM_Plex_Mono'] font-bold text-[#78716C]">#{offer.rank}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${risk.bg} ${risk.text}`}>
                  {risk.label}
                </span>
              </div>
              <h4 className="text-base font-['Playfair_Display'] font-semibold text-[#1C1917]">
                {offer.lender_name}
              </h4>
              <p className="text-xs text-[#78716C] mb-3">{offer.product_type}</p>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#78716C]">Monthly Payment</span>
                  <span className="font-['IBM_Plex_Mono'] font-semibold">{fmtMoney(offer.monthly_debt_service)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#78716C]">Total Cost</span>
                  <span className="font-['IBM_Plex_Mono'] font-semibold text-[#E07A5F]">{fmtMoney(offer.total_financing_cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#78716C]">New DSCR</span>
                  <span className={`font-['IBM_Plex_Mono'] font-semibold ${
                    offer.new_dscr >= 1.5 ? "text-[#2D4A3E]" : offer.new_dscr >= 1.15 ? "text-[#E3B505]" : "text-[#E07A5F]"
                  }`}>
                    {offer.new_dscr >= 99 ? "N/A" : `${offer.new_dscr}x`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#78716C]">Post-Loan FCF</span>
                  <span className={`font-['IBM_Plex_Mono'] font-semibold ${
                    offer.post_funding_free_cash_flow >= 0 ? "text-[#2D4A3E]" : "text-[#E07A5F]"
                  }`}>
                    {fmtMoney(offer.post_funding_free_cash_flow)}
                  </span>
                </div>
              </div>

              <div className={`mt-4 flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium ${rec.bg} ${rec.color}`}>
                <RecIcon className="w-4 h-4" />
                {offer.recommendation}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed Comparison Table */}
      <div className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
        <div className="p-4 border-b border-[#E7E5DF]">
          <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
            Detailed Comparison
          </h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F1EFEA]">
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Rank</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Lender</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Product</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Loan Amt</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Net Fund</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Mo. Payment</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Total Cost</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Est. APR</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">New DSCR</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Risk</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Verdict</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparison.map((o) => {
                const risk = riskStyles[o.risk_level] || riskStyles.High;
                return (
                  <TableRow key={o.id} className="table-row-hover">
                    <TableCell className="font-['IBM_Plex_Mono'] font-bold text-[#1A362D] py-4">#{o.rank}</TableCell>
                    <TableCell className="font-semibold text-[#1C1917] py-4">{o.lender_name}</TableCell>
                    <TableCell className="text-sm text-[#57534E] py-4">{o.product_type}</TableCell>
                    <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right py-4">{fmtMoney(o.loan_amount)}</TableCell>
                    <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right py-4">{fmtMoney(o.net_funding_amount)}</TableCell>
                    <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right font-semibold py-4">{fmtMoney(o.monthly_debt_service)}</TableCell>
                    <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right text-[#E07A5F] font-semibold py-4">{fmtMoney(o.total_financing_cost)}</TableCell>
                    <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right py-4">{o.estimated_apr ? `${o.estimated_apr}%` : "—"}</TableCell>
                    <TableCell className={`font-['IBM_Plex_Mono'] text-sm text-right font-semibold py-4 ${
                      o.new_dscr >= 1.5 ? "text-[#2D4A3E]" : o.new_dscr >= 1.15 ? "text-[#E3B505]" : "text-[#E07A5F]"
                    }`}>
                      {o.new_dscr >= 99 ? "N/A" : `${o.new_dscr}x`}
                    </TableCell>
                    <TableCell className="py-4">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${risk.bg} ${risk.text}`}>
                        {o.risk_level}
                      </span>
                    </TableCell>
                    <TableCell className="py-4">
                      <Badge variant={o.recommendation === "Good Fit" ? "default" : o.recommendation === "Borderline" ? "secondary" : "destructive"}>
                        {o.recommendation}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
